/**
 * What arrived in an activation: a one-time code, or a verification link.
 *
 * Both used to render as the same short green pill, which was fine while every
 * delivery was six digits. It is not: plenty of services confirm by link, and a
 * 160-character URL in a pill overflows, cannot be opened, and looks broken. Measured
 * against a real Supabase signup on 2026-08-17.
 */
import { Check, Copy, ExternalLink } from "lucide-react";

import { useI18n } from "@/lib/i18n";

export function isLinkCode(code: string): boolean {
  return /^https?:\/\//i.test(code.trim());
}

/**
 * A whole message rather than a code: what an email delivers when it carries neither an
 * OTP nor a link (the provider charges for it and will not refund it, so the buyer
 * gets the text). Codes are short and have no spaces.
 */
export function isMessageCode(code: string): boolean {
  const c = code.trim();
  return !isLinkCode(c) && (c.length > 24 || /\s/.test(c));
}

export function DeliveredCode({ code, onCopy }: { code: string; onCopy: (v: string) => void }) {
  const { t } = useI18n();

  if (isMessageCode(code)) {
    return (
      <div className="mt-2 space-y-2 rounded-xl border border-emerald-500/20 bg-emerald-500/10 p-3">
        <div className="flex items-center gap-1.5 text-xs font-bold text-emerald-400">
          <Check className="h-3.5 w-3.5 shrink-0" />
          {t("activations.message_received")}
        </div>
        <p className="max-h-40 overflow-y-auto whitespace-pre-wrap break-words rounded-lg bg-muted/60 p-2 text-xs select-all">
          {code}
        </p>
        <button
          onClick={() => onCopy(code)}
          className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-500/20 px-3 py-1.5 text-xs font-semibold text-emerald-400 transition-colors hover:bg-emerald-500/10"
        >
          <Copy className="h-3.5 w-3.5" />
          {t("common.copy")}
        </button>
      </div>
    );
  }

  if (!isLinkCode(code)) {
    return (
      <button
        onClick={() => onCopy(code)}
        className="mt-2 inline-flex items-center gap-2 rounded-xl border border-emerald-500/20 bg-emerald-500/10 px-3 py-1.5 text-sm font-bold text-emerald-400 transition-colors hover:bg-emerald-500/20"
      >
        <Check className="h-4 w-4 shrink-0" />
        {t("activations.code_label")} {code}
        <Copy className="h-3.5 w-3.5 shrink-0" />
      </button>
    );
  }

  return (
    <div className="mt-2 space-y-2 rounded-xl border border-emerald-500/20 bg-emerald-500/10 p-3">
      <div className="flex items-center gap-1.5 text-xs font-bold text-emerald-400">
        <Check className="h-3.5 w-3.5 shrink-0" />
        {t("activations.verification_link")}
      </div>
      {/* Shown in full and selectable: some flows need the URL pasted into the browser
          the account was created in, not opened here. */}
      <code className="block break-all rounded-lg bg-muted/60 p-2 font-mono text-[11px] select-all">
        {code}
      </code>
      <div className="flex flex-wrap gap-2">
        <a
          href={code}
          target="_blank"
          rel="noopener noreferrer nofollow"
          className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-500/20 px-3 py-1.5 text-xs font-semibold text-emerald-300 transition-colors hover:bg-emerald-500/30"
        >
          <ExternalLink className="h-3.5 w-3.5" />
          {t("activations.open_link")}
        </a>
        <button
          onClick={() => onCopy(code)}
          className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-500/20 px-3 py-1.5 text-xs font-semibold text-emerald-400 transition-colors hover:bg-emerald-500/10"
        >
          <Copy className="h-3.5 w-3.5" />
          {t("common.copy")}
        </button>
      </div>
    </div>
  );
}
